import { isNsirClosed, statusDescription } from '../constants/status';

/**
 * Conflitos (409) do `DELETE /nsirs/{id}`.
 * Contrato: docs/iniciativas/exclusao-nsir/contratos/api-contract.md
 *
 * O backend devolve `motivos` (lista de códigos) no corpo do erro — podem vir vários de
 * uma vez. Todos são contornáveis com `force=true`; o que muda é o atrito que o front
 * impõe antes de reenviar.
 */
export const NSIR_DELETE_MOTIVO = {
  DIGITACAO: 'DIGITACAO',
  SUCATA: 'SUCATA',
  PROGRAMACAO_DIARIA: 'PROGRAMACAO_DIARIA',
};

/** Atrito que o front deve impor antes de reenviar com `force=true`. */
export const NSIR_DELETE_CONFLICT = {
  /** Dado operacional em jogo: sugerir cancelar a NSIR e exigir dupla confirmação. */
  RISCO_ALTO: 'RISCO_ALTO',
  /** Vínculo simples: uma confirmação basta. */
  CONFIRMACAO: 'CONFIRMACAO',
};

/**
 * Motivo do conflito em texto curto, para listar no diálogo.
 * A cópia é do front — a `mensagem` do backend concatena as frases de todos os
 * motivos num parágrafo só, e só serve de fallback quando `motivos` não vem.
 */
export const NSIR_DELETE_MOTIVO_DESCRICAO = {
  [NSIR_DELETE_MOTIVO.DIGITACAO]: 'Possui digitação registrada.',
  [NSIR_DELETE_MOTIVO.SUCATA]: 'Possui pesagem de sucata registrada.',
  [NSIR_DELETE_MOTIVO.PROGRAMACAO_DIARIA]: 'Possui vínculo com Programação Diária.',
};

/**
 * Motivos reconhecidos, na ordem em que o backend mandou.
 * Vazio quando `motivos` não veio (backend antigo) ou traz códigos desconhecidos.
 *
 * @param {string[]} motivos
 * @returns {string[]}
 */
export const getMotivosDescricao = motivos => (Array.isArray(motivos) ? motivos : [])
  .map(motivo => NSIR_DELETE_MOTIVO_DESCRICAO[motivo])
  .filter(Boolean);

/**
 * O que o usuário perde ao insistir na exclusão, por motivo.
 * Mostrado na confirmação definitiva dos casos de risco alto.
 */
export const NSIR_DELETE_MOTIVO_CONSEQUENCIA = {
  [NSIR_DELETE_MOTIVO.DIGITACAO]: 'A digitação registrada será apagada. Se ela já foi enviada ao sistema terceiro, o registro continuará lá — não existe estorno automático.',
  [NSIR_DELETE_MOTIVO.SUCATA]: 'A pesagem de sucata registrada será apagada junto com a NSIR.',
  [NSIR_DELETE_MOTIVO.PROGRAMACAO_DIARIA]: 'O vínculo com a Programação Diária será removido.',
};

const CONSEQUENCIA_GENERICA = 'Os registros vinculados à NSIR serão apagados.';

/**
 * Lista de consequências a exibir na confirmação definitiva.
 * Sempre devolve ao menos uma linha, mesmo sem `motivos` reconhecidos.
 *
 * @param {string[]} motivos
 * @returns {string[]}
 */
export const getConsequenciasExclusao = motivos => {
  const consequencias = (Array.isArray(motivos) ? motivos : [])
    .map(motivo => NSIR_DELETE_MOTIVO_CONSEQUENCIA[motivo])
    .filter(Boolean);

  return consequencias.length > 0 ? consequencias : [CONSEQUENCIA_GENERICA];
};

/**
 * Aviso para NSIR já fechada (cancelada ou finalizada no GDIS), em que o cancelamento
 * deixa de ser alternativa. `null` quando a NSIR ainda está aberta.
 *
 * @param {string} status
 * @returns {string|null}
 */
export const getAvisoNsirFechada = status => {
  if (!isNsirClosed(status)) return null;

  // Só a primeira letra cai para minúscula: "Finalizada no GDIS" não pode virar "gdis".
  const descricao = statusDescription(status) || '';
  const situacao = descricao ? descricao.charAt(0).toLowerCase() + descricao.slice(1) : 'fechada';

  return `Esta NSIR já está ${situacao} — cancelar não se aplica. A única ação restante é excluir.`;
};

/** Texto usado quando o backend não devolve `mensagem` nem `motivos` no 409. */
export const NSIR_DELETE_CONFLICT_FALLBACK = {
  [NSIR_DELETE_CONFLICT.RISCO_ALTO]: 'A NSIR possui registros operacionais vinculados.',
  [NSIR_DELETE_CONFLICT.CONFIRMACAO]: 'NSIR possui vínculo com Programação Diária.',
};

const MOTIVOS_RISCO_ALTO = [NSIR_DELETE_MOTIVO.DIGITACAO, NSIR_DELETE_MOTIVO.SUCATA];

const normalize = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

/**
 * Compatibilidade com backend ainda sem o campo `motivos`: classifica pelo texto.
 */
const resolveByMensagem = data => {
  const mensagem = normalize(data.mensagem || data.mensagemDetalhada);
  if (!mensagem) return null;

  if (mensagem.includes('digitacao') || mensagem.includes('sucata')) {
    return NSIR_DELETE_CONFLICT.RISCO_ALTO;
  }
  if (mensagem.includes('programacao diaria')) return NSIR_DELETE_CONFLICT.CONFIRMACAO;

  return null;
};

/**
 * Classifica o corpo de um 409 do delete de NSIR.
 *
 * Precedência: `DIGITACAO`/`SUCATA` vencem `PROGRAMACAO_DIARIA` quando vêm no mesmo
 * array — o atrito maior engloba o menor.
 *
 * @param {{ motivos?: string[], mensagem?: string, mensagemDetalhada?: string }} data
 * @returns {string|null} valor de `NSIR_DELETE_CONFLICT` ou null se não reconhecido
 */
export const resolveNsirDeleteConflict = data => {
  if (!data) return null;

  const motivos = Array.isArray(data.motivos) ? data.motivos : [];

  if (motivos.some(motivo => MOTIVOS_RISCO_ALTO.includes(motivo))) {
    return NSIR_DELETE_CONFLICT.RISCO_ALTO;
  }
  if (motivos.includes(NSIR_DELETE_MOTIVO.PROGRAMACAO_DIARIA)) {
    return NSIR_DELETE_CONFLICT.CONFIRMACAO;
  }
  if (motivos.length > 0) return null;

  return resolveByMensagem(data);
};
